<template>
	<div>
		<b-modal ref="modal" hide-footer no-close-on-backdrop @hide="onModalHide">
			<template v-slot:modal-title>
				{{ modal_title }}
			</template>

			<!-- CHARGEMENT -->
			<div v-if="current_step === null">
				<LoadingSpinner class="col-12" />
			</div>

			<!-- ÉTAPE : Confirmation personne physique -->
			<div v-else-if="current_step === 'confirm_physique'">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'user']" class="mr-3 mt-1 icon-step-lg-primary" />
					<p class="mb-0">{{ $t('tiers.confirm_physique_question') }}</p>
				</div>
				<div class="d-flex flex-column">
					<b-button variant="primary" class="mb-2" @click.prevent="on_confirm_physique(true)">
						<font-awesome-icon :icon="['fal', 'user-check']" class="mr-1" />
						{{ $t('tiers.confirm_physique_yes') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="on_confirm_physique(false)">
						<font-awesome-icon :icon="['fal', 'building']" class="mr-1" />
						{{ $t('tiers.confirm_physique_no') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Saisie du SIRET -->
			<div v-else-if="current_step === 'siret_input'">
				<p class="text-muted small mb-3">{{ $t('tiers.saisir_siret') }}</p>
				<div class="form-group">
					<label class="col-form-label">{{ $t('tiers.siret') }} *</label>
					<input
						type="text"
						class="form-control"
						v-model="siret_input_value"
						:placeholder="$t('tiers.siret_placeholder')"
						maxlength="14"
						:class="{ 'is-invalid': siret_error }"
						@keyup.enter="submit_siret"
					/>
					<ErrorAlert v-if="siret_error" :messageI18n="siret_error" />
				</div>
				<div class="text-center mt-3">
					<b-button variant="primary" @click.prevent="submit_siret" :disabled="siret_loading">
						<font-awesome-icon v-if="siret_loading" :icon="['fas', 'spinner']" pulse class="mr-1" />
						{{ $t('global.valider') }}
						<font-awesome-icon v-if="!siret_loading" :icon="['fal', 'search']" class="ml-1" />
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Confirmation des données Pappers -->
			<div v-else-if="current_step === 'pappers_confirm'">
				<p class="mb-3">{{ $t('tiers.pappers_found_desc') }}</p>
				<div class="card mb-3">
					<div class="card-body p-3">
						<dl class="row mb-0 small">
							<dt class="col-5 text-muted">{{ $t('tiers.siret') }}</dt>
							<dd class="col-7"><strong>{{ pappers_result && pappers_result.siret || siret_input_value }}</strong></dd>
							<dt class="col-5 text-muted">{{ $t('tiers.rs') }}</dt>
							<dd class="col-7">{{ pappers_result.nom_entreprise }}</dd>
							<dt class="col-5 text-muted">{{ $t('tiers.address1') }}</dt>
							<dd class="col-7">{{ pappers_result.siege && pappers_result.siege.adresse_ligne_1 }}</dd>
							<dt class="col-5 text-muted">{{ $t('tiers.postalcode') }}</dt>
							<dd class="col-7">{{ pappers_result.siege && pappers_result.siege.code_postal }}</dd>
							<dt class="col-5 text-muted">{{ $t('tiers.town') }}</dt>
							<dd class="col-7">{{ pappers_result.siege && pappers_result.siege.ville }}</dd>
						</dl>
					</div>
				</div>
				<div class="d-flex">
					<b-button variant="primary" class="flex-grow-1 mr-2" @click.prevent="confirm_pappers" :disabled="processing">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'check']" class="mr-1" />
						{{ $t('tiers.pappers_confirm_btn') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="cancel_pappers">
						{{ $t('global.annuler') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : SIRET déjà utilisé sur la licence par un autre client -->
			<div v-else-if="current_step === 'siret_exists'">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'exclamation-circle']" class="text-danger mr-3 mt-1 icon-step-lg" />
					<p class="mb-0">{{ $t('tiers.siret_already_exists') }}</p>
				</div>
				<div class="d-flex flex-column">
					<b-button variant="primary" class="mb-2" @click.prevent="go_contact_us">
						<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
						{{ $t('monte.contact_us') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="on_impossible_choose_other">
						<font-awesome-icon :icon="['fal', 'user-times']" class="mr-1" />
						{{ $t('monte.choose_other_client') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : TVA intracommunautaire déjà utilisée sur la licence par un autre client -->
			<div v-else-if="current_step === 'vat_exists'">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'exclamation-circle']" class="text-danger mr-3 mt-1 icon-step-lg" />
					<p class="mb-0">{{ $t('tiers.vat_already_exists') }}</p>
				</div>
				<div class="d-flex flex-column">
					<b-button variant="primary" class="mb-2" @click.prevent="go_contact_us">
						<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
						{{ $t('monte.contact_us') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="on_impossible_choose_other">
						<font-awesome-icon :icon="['fal', 'user-times']" class="mr-1" />
						{{ $t('monte.choose_other_client') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : SIRET non reconnu / société inactive -->
			<div v-else-if="current_step === 'pappers_impossible'">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'exclamation-triangle']" class="text-warning mr-3 mt-1 icon-step-lg" />
					<p class="mb-0">{{ $t('tiers.pappers_not_found') }}</p>
				</div>
				<div class="d-flex flex-column">
					<b-button variant="primary" class="mb-2" @click.prevent="go_contact_us">
						<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
						{{ $t('monte.contact_us') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="on_impossible_choose_other">
						<font-awesome-icon :icon="['fal', 'user-times']" class="mr-1" />
						{{ $t('monte.choose_other_client') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Formulaire de complétion des champs manquants -->
			<div v-else-if="current_step === 'form'">
				<form @submit.prevent="checkForm" id="addTiers" autocomplete="off">
					<div class="row">
						<div class="col-12" v-if="form_message !== ''">
							<ErrorAlert :messageI18n="form_message" />
						</div>

						<div class="col-12">
							<WarnAlert v-if="tier_already_exists" messageI18n="tiers.already_exist" />
						</div>

						<!-- Tiers moral : raison sociale éditable -->
						<div v-if="effective_tiers_moral == 1" class="col-12">
							<div class="form-group">
								<label for="tiers_rs" class="col-form-label">{{ $t("tiers.rs") }} *</label>
								<input type="text" id="tiers_rs" class="form-control" v-model="form.tiers_rs" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('rs') > -1 }">
							</div>
						</div>

						<!-- Tiers physique : civilité, prénom, nom -->
						<div v-else class="col-12">
							<div class="form-group">
								<label for="tiers_civility" class="col-form-label">{{ $t("tiers.civility") }} *</label>
								<e-select
									id="tiers_civility"
									v-model="form.tiers_civility"
									:options="civilities"
									:allow-empty="false"
									:show-labels="false"
									:disabled="readonly.indexOf('civility') > -1"
								/>
							</div>
							<div class="row">
								<div class="col-6">
									<div class="form-group">
										<label for="tiers_firstname" class="col-form-label">{{ $t("tiers.firstname") }} *</label>
										<input type="text" id="tiers_firstname" class="form-control" v-model="form.tiers_firstname" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('firstname') > -1 }" :disabled="readonly.indexOf('firstname') > -1">
									</div>
								</div>
								<div class="col-6">
									<div class="form-group">
										<label for="tiers_lastname" class="col-form-label">{{ $t("tiers.lastname") }} *</label>
										<input type="text" id="tiers_lastname" class="form-control" v-model="form.tiers_lastname" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('lastname') > -1 }" :disabled="readonly.indexOf('lastname') > -1">
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-12">
							<div class="form-group">
								<label for="tiers_address1" class="col-form-label">{{ $t("tiers.address1") }} *</label>
								<input type="text" id="tiers_address1" class="form-control" v-model="form.tiers_address1" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('address1') > -1 }" :disabled="readonly.indexOf('address1') > -1">
							</div>
						</div>
						<div class="col-12">
							<div class="form-group">
								<label for="tiers_address2" class="col-form-label">{{ $t("tiers.address2") }}</label>
								<input type="text" id="tiers_address2" class="form-control" v-model="form.tiers_address2" autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('address2') > -1 }" :disabled="readonly.indexOf('address2') > -1">
							</div>
						</div>
						<div class="col-4">
							<div class="form-group">
							<label for="tiers_postalcode" class="col-form-label">{{ postalcode_label }} *</label>
								<input type="text" id="tiers_postalcode" class="form-control" v-model="form.tiers_postalcode" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('postalcode') > -1 }" :disabled="readonly.indexOf('postalcode') > -1">
							</div>
						</div>
						<div class="col-8">
							<div class="form-group">
								<label for="tiers_town" class="col-form-label">{{ $t("tiers.town") }} *</label>
								<input type="text" id="tiers_town" class="form-control" v-model="form.tiers_town" required autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('town') > -1 }" :disabled="readonly.indexOf('town') > -1">
							</div>
						</div>					
						<div v-if="hasRegions" class="col-12">
							<div class="form-group">
								<label for="tiers_region" class="col-form-label">{{ $t("tiers.region") }} *</label>
								<e-select
									id="tiers_region"
									v-model="form.tiers_region"
									:options="currentRegions"
									:allow-empty="true"
									:show-labels="false"
									:class="{ 'is-invalid': error && error.indexOf('region') > -1 }"
								/>
							</div>
						</div>
						<div class="col-12">
							<div class="form-group">
								<label for="tiers_country" class="col-form-label">{{ $t("tiers.country") }} *</label>
								<CountryInput v-model="form.tiers_country" :disabled="readonly.indexOf('country') > -1"></CountryInput>
							</div>
						</div>
						<div v-if="effective_tiers_moral == 1 && is_vat_country" class="col-12">
							<div class="form-group">
							<label for="tiers_vat_number" class="col-form-label">{{ $t("tiers.tiers_vat_number") }} *</label>
								<input type="text" id="tiers_vat_number" class="form-control" v-model="form.tiers_vat_number" autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('vat_number') > -1 }" :disabled="readonly.indexOf('vat_number') > -1">
								<ErrorAlert v-if="!check_format_vat" messageI18n="tiers.tiers_vat_number_format" />
								<ErrorAlert v-if="check_vat !== '1'" messageI18n="tiers.tiers_vat_number_check" />
							</div>
						</div>
						<div class="col-12">
							<div class="form-group">
								<label for="phone_number" class="col-form-label">{{ $t("tiers.phone_number") }} *</label>
								<div class="row no-gutters phone">
									<div class="col-auto pr-2" :style="readonly.indexOf('phone_number') > -1 ? 'pointer-events:none;opacity:0.65' : ''">
										<phoneInput v-model="phone"></phoneInput>
									</div>
									<div class="col pr-2">
										<div class="input-group">
											<div class="input-group-prepend">
												<div class="input-group-text">{{ phone.phone_indicatif }}</div>
											</div>
											<input type="text" class="form-control" v-model="phone.phone_numero" required autocomplete="chrome-off" :disabled="readonly.indexOf('phone_number') > -1" :class="{ 'is-invalid': error && error.indexOf('phone') > -1 }">
										</div>
									</div>
								</div>
								<ErrorAlert v-if="phone_invalid_format" messageI18n="toast.phone_not_valid" />
								<ErrorAlert v-else-if="error.indexOf('phone') > -1" messageI18n="formulaire.erreur_champs_non_remplis" />
							</div>
						</div>
					</div>
				</form>

				<div class="text-center">
					<b-button variant="primary" @click.prevent="checkForm">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
						{{ $t("global.valider") }}
						<font-awesome-icon :icon="['fal', 'check']"/>
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Téléphone uniquement (après confirmation Pappers) -->
			<div v-else-if="current_step === 'phone_only'">
				<p class="text-muted small mb-3">{{ $t('tiers.saisir_telephone') }}</p>
				<div class="form-group">
					<label class="col-form-label">{{ $t("tiers.phone_number") }} *</label>
					<div class="row no-gutters phone">
						<div class="col-auto pr-2">
							<phoneInput v-model="phone"></phoneInput>
						</div>
						<div class="col pr-2">
							<div class="input-group">
								<div class="input-group-prepend">
									<div class="input-group-text">{{ phone.phone_indicatif }}</div>
								</div>
								<input type="text" class="form-control" v-model="phone.phone_numero" autocomplete="chrome-off" :class="{ 'is-invalid': error && error.indexOf('phone') > -1 }">
							</div>
						</div>
					</div>
					<ErrorAlert v-if="phone_invalid_format" messageI18n="toast.phone_not_valid" />
					<ErrorAlert v-else-if="error.indexOf('phone') > -1" messageI18n="formulaire.erreur_champs_non_remplis" />
				</div>
				<div class="text-center">
					<b-button variant="primary" @click.prevent="submit_phone_only" :disabled="processing">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
						{{ $t("global.valider") }}
						<font-awesome-icon :icon="['fal', 'check']"/>
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Sélection du pays (mode création entreprise) -->
			<div v-else-if="current_step === 'create_country_select'">
				<p class="text-muted small mb-3">{{ $t('tiers.create_select_country') }}</p>
				<div class="form-group">
					<label class="col-form-label">{{ $t('tiers.country') }} *</label>
					<CountryInput v-model="create_country" />
					<ErrorAlert v-if="error.indexOf('country') > -1" messageI18n="formulaire.erreur_champs_non_remplis" />
				</div>
				<div class="text-center mt-3">
					<b-button variant="primary" @click.prevent="submit_create_country">
						{{ $t('global.valider') }}
						<font-awesome-icon :icon="['fal', 'arrow-right']" class="ml-1" />
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Pays non supporté (mode création) -->
			<div v-else-if="current_step === 'create_country_blocked'">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'exclamation-circle']" class="text-danger mr-3 mt-1 icon-step-lg" />
					<p class="mb-0">{{ $t('tiers.create_country_blocked') }}</p>
				</div>
				<div class="d-flex flex-column">
					<b-button variant="primary" class="mb-2" @click.prevent="go_contact_us">
						<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
						{{ $t('monte.contact_us') }}
					</b-button>
					<b-button variant="outline-secondary" @click.prevent="$refs['modal'].hide()">
						<font-awesome-icon :icon="['fal', 'times']" class="mr-1" />
						{{ $t('global.annuler') }}
					</b-button>
				</div>
			</div>

			<!-- ÉTAPE : Formulaire de création entreprise (hors France) -->
			<div v-else-if="current_step === 'create_form'">
				<div v-if="form_message !== ''" class="mb-3">
					<ErrorAlert :messageI18n="form_message" />
				</div>
				<div class="row">
					<div class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.rs') }} *</label>
							<input type="text" class="form-control" v-model="form.tiers_rs" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('rs') > -1 }" />
						</div>
					</div>
					<div class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.address1') }} *</label>
							<input type="text" class="form-control" v-model="form.tiers_address1" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('address1') > -1 }" />
						</div>
					</div>
					<div class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.address2') }}</label>
							<input type="text" class="form-control" v-model="form.tiers_address2" autocomplete="chrome-off" />
						</div>
					</div>
					<div class="col-4">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.postalcode') }} *</label>
							<input type="text" class="form-control" v-model="form.tiers_postalcode" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('postalcode') > -1 }" />
						</div>
					</div>
					<div class="col-8">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.town') }} *</label>
							<input type="text" class="form-control" v-model="form.tiers_town" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('town') > -1 }" />
						</div>
					</div>
					<div v-if="hasRegions" class="col-12">
						<div class="form-group">
							<label for="tiers_region_create" class="col-form-label">{{ $t('tiers.region') }} *</label>
							<b-form-select
								id="tiers_region_create"
								v-model="form.tiers_region"
								:options="currentRegions"
								value-field="value"
								text-field="label"
								:class="{ 'is-invalid': error.indexOf('region') > -1 }"
							/>
						</div>
					</div>
					<div class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.country') }}</label>
							<CountryInput v-model="form.tiers_country" :disabled="true" />
						</div>
					</div>
					<div v-if="is_vat_country" class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.tiers_vat_number') }} *</label>
							<input type="text" class="form-control" v-model="form.tiers_vat_number" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('vat_number') > -1 }" />
							<ErrorAlert v-if="!check_format_vat" messageI18n="tiers.tiers_vat_number_format" />
						</div>
					</div>
					<div class="col-12">
						<div class="form-group">
							<label class="col-form-label">{{ $t('tiers.phone_number') }} *</label>
							<div class="row no-gutters phone">
								<div class="col-auto pr-2">
									<phoneInput v-model="phone"></phoneInput>
								</div>
								<div class="col pr-2">
									<div class="input-group">
										<div class="input-group-prepend">
											<div class="input-group-text">{{ phone.phone_indicatif }}</div>
										</div>
										<input type="text" class="form-control" v-model="phone.phone_numero" autocomplete="chrome-off" :class="{ 'is-invalid': error.indexOf('phone') > -1 }">
									</div>
								</div>
							</div>
							<ErrorAlert v-if="phone_invalid_format" messageI18n="toast.phone_not_valid" />
							<ErrorAlert v-else-if="error.indexOf('phone') > -1" messageI18n="formulaire.erreur_champs_non_remplis" />
						</div>
					</div>
				</div>
				<div class="text-center mt-2">
					<b-button variant="primary" @click.prevent="submit_create_form" :disabled="processing">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse class="mr-1" />
						<template v-else>
							{{ $t('global.valider') }}
							<font-awesome-icon :icon="['fal', 'check']" class="ml-1" />
						</template>
					</b-button>
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script type="text/javascript">
	import Vue from 'vue'
	import Tiers from '@/mixins/Tiers.js'
	import _cloneDeep from 'lodash/cloneDeep';
	import _isEmpty from 'lodash/isEmpty'
	import Indicatif from 'GroomyRoot/assets/lang/countries/indicatif'
    import Countries from '@/assets/lang/countries/countries'
    import Regions from '@/assets/lang/countries/regions'

	export default {
		name: "InformationsTiersForm",
		mixins: [Tiers],
		props: {
			allowed_countries: {
				type: Array,
				default: () => []
			}
		},
		data () {
			return {
				current_step: null, // 'confirm_physique' | 'siret_input' | 'pappers_confirm' | 'pappers_impossible' | 'form' | 'phone_only'
				processing: false,
                form_message: "",
                tier_already_exists: false,
				form: {},
				countries: {},
				civilities: [
					this.getTrad('civility.m'),
					this.getTrad('civility.mme')
				],
				default_form: {
					tiers_rs: '',
					tiers_firstname: '',
					tiers_lastname: '',
					tiers_civility: this.getTrad('civility.m'),
					tiers_address1: '',
					tiers_address2: '',
					tiers_postalcode: '',
					tiers_town: '',
					tiers_country: '75',
					tiers_vat_number: '',
					tiers_region: ''
				},
				regionWhitelist: [104],
				error: [],
				readonly: [],
				errors: [],
				phone: {
					phone_indicatif: '+33',
					phone_numero: '',
					phone_country: 'FRA'
				},
				check_format_vat: true,
				check_vat: "1",
				phone_invalid_format: false,
				treat_as_morale: false,
				siret_input_value: '',
				siret_error: '',
				siret_loading: false,
				pappers_result: null,
				pending_pappers: null, // Données Pappers en attente du téléphone
				create_mode: false,	 // Mode création d'un nouveau tiers entreprise
				create_country: '',
			}
		},
		computed: {
			/**
			 * Retourne 1 si le tiers doit être traité comme une personne morale
			 * (soit nativement, soit parce que l'utilisateur l'a choisi).
			 */
			effective_tiers_moral() {
				return this.treat_as_morale ? 1 : (Number(this.form.tiers_moral) || 0)
			},
			/** Retourne true si le pays du tiers possède des régions/comtés à sélectionner. */
			hasRegions() {
				return this.regionWhitelist.includes(parseInt(this.form.tiers_country))
			},
			/** Liste des régions/comtés pour le pays courant. */
			currentRegions() {
				return Regions[parseInt(this.form.tiers_country)] || []
			},
			/** Label du code postal selon le pays (Eircode pour l'Irlande). */
			postalcode_label() {
				return parseInt(this.form.tiers_country) === 104 ? this.$t('tiers.eircode') : this.$t('tiers.postalcode')
			},
			/**
			 * Retourne true si le pays du tiers est soumis à TVA intracommunautaire
			 * (pays UE hors France). Se base sur la liste de getCountryVatRegex().
			 */
			is_vat_country() {
				const country = parseInt(this.form.tiers_country)
				return country !== 75 && Object.prototype.hasOwnProperty.call(this.getCountryVatRegex(), String(country))
			},
			/** Titre de la modale selon l'étape courante. */
			modal_title() {
				const map = {
					confirm_physique:       this.$t('global.my_details'),
					siret_input:            this.$t('tiers.siret'),
					pappers_confirm:        this.$t('tiers.pappers_found_title'),
					pappers_impossible:     this.$t('tiers.pappers_impossible_title'),
					form:                   this.$t('global.my_details'),
					phone_only:             this.$t('tiers.phone_number'),
					create_country_select:  this.$t('tiers.create_company'),
					create_country_blocked: this.$t('tiers.create_company'),
					create_form:            this.$t('tiers.create_company'),
				}
				return map[this.current_step] || this.$t('global.my_details')
			},
		},
		methods: {
			/**
			 * Intercepte la fermeture de la modale.
			 * Si l'utilisateur ferme via la croix ou la touche Échap, émet 'cancel'
			 * pour permettre au parent de réinitialiser proprement le tiers sélectionné
			 * sans afficher de message d'erreur.
			 * @param {Object} bvModalEvt - Événement BootstrapVue (trigger = 'headerclose' | 'esc' | 'backdrop' | 'event' | undefined).
			 */
			onModalHide(bvModalEvt) {
				if (bvModalEvt && (bvModalEvt.trigger === 'headerclose' || bvModalEvt.trigger === 'esc')) {
					this.$emit('cancel')
				}
			},

			/**
			 * Ouvre la modale en mode création d'un nouveau tiers entreprise.
			 */
			openCreateModal() {
				this.form = { ...this.default_form, tiers_moral: 1 }
				this.treat_as_morale = false
				this.create_mode = true
				this.create_country = ''
				this.siret_input_value = ''
				this.pappers_result = null
				this.pending_pappers = null
				this.form_message = ''
				this.processing = false
				this.error = []
				this.phone_invalid_format = false
				this.phone = { phone_indicatif: '+33', phone_numero: '', phone_country: 'FRA' }
				this.current_step = 'create_country_select'
				this.$refs['modal'].show()
			},

			/**
			 * Valide la sélection du pays et redirige vers la bonne étape.
			 * France → saisie SIRET (Pappers), pays UE → formulaire avec TVA, autre → bloqué.
			 */
			submit_create_country() {
				this.error = []
				if (!this.create_country) {
					this.error = ['country']
					return
				}
				const countryId = String(this.create_country)

				// Vérifier si le pays est autorisé pour les contrats (politique de l'haras)
				if (this.allowed_countries.length > 0 && !this.allowed_countries.includes(parseInt(countryId))) {
					this.$refs['modal'].hide()
					this.$emit('country-blocked')
					return
				}

				const isEU = Object.prototype.hasOwnProperty.call(this.getCountryVatRegex(), countryId)
				const isFrance = countryId === '75'
                const isAllowed = this.allowed_countries.length > 0 && this.allowed_countries.includes(parseInt(countryId))

                if (!isFrance && !isEU && !isAllowed) {
					this.current_step = 'create_country_blocked'
					return
				}

				this.form.tiers_country = this.create_country
				this._init_phone()

				if (isFrance) {
					this.current_step = 'siret_input'
				} else {
					this.current_step = 'create_form'
				}
			},

			/**
			 * Valide et soumet le formulaire de création entreprise (hors France).
			 */
			async submit_create_form() {
				if (this.processing) return
				this.error = []
				this.form_message = ''
				this.phone_invalid_format = false
				this.check_format_vat = true

				if (_isEmpty(this.form.tiers_rs)) this.error.push('rs')
				if (_isEmpty(this.form.tiers_address1)) this.error.push('address1')
				if (_isEmpty(this.form.tiers_postalcode)) this.error.push('postalcode')
				if (_isEmpty(this.form.tiers_town)) this.error.push('town')
				if (this.hasRegions && _isEmpty(this.form.tiers_region)) this.error.push('region')
				if (_isEmpty(this.phone.phone_numero)) this.error.push('phone')
				if (this.is_vat_country && _isEmpty(this.form.tiers_vat_number)) this.error.push('vat_number')

				this.form.tiers_vat_number = (this.form.tiers_vat_number || '').replace(/\W/g, '').toUpperCase()
				if (this.form.tiers_vat_number && !this.checkVAT(this.form.tiers_vat_number, this.form.tiers_country)) {
					this.check_format_vat = false
					this.error.push('vat_number')
				}

				if (this.error.length > 0) {
					this.form_message = 'formulaire.erreur_champs_non_remplis'
					return
				}

				this.processing = true
				const params = {
					tiers_rs:         this.form.tiers_rs,
					tiers_address1:   this.form.tiers_address1,
					tiers_address2:   this.form.tiers_address2 || '',
					tiers_postalcode: this.form.tiers_postalcode,
					tiers_town:       this.form.tiers_town,
					tiers_region:     this.form.tiers_region || '',
					tiers_country:    this.form.tiers_country,
					tiers_vat_number: this.form.tiers_vat_number || '',
					phone:            { ...this.phone },
					tiers_lang:       this.$store.state.user.lang,
				}

				const result = await this.createTiersEntreprise(params)
				this.processing = false

				if (result === 'phone_invalid') {
					this.error = ['phone']
					this.phone_invalid_format = true
				} else if (result === 'vat_duplicate') {
					this.current_step = 'vat_exists'
				} else if (result) {
					this.$refs['modal'].hide()
					this.$emit('submit', result)
				} else {
					this.failureToast('toast.info_save_failed')
					this.$emit('submit', false)
				}
			},
			/**
			 * Point d'entrée principal. Initialise le formulaire puis détermine
			 * l'étape à afficher selon la nature et la complétude du tiers.
			 * @param {Object} tiers - L'objet tiers à contrôler.
			 */
			async openModal(tiers) {
				this.form            = _cloneDeep(tiers)
				this.treat_as_morale = false
				this.siret_input_value = tiers.tiers_siret || ''
				this.current_step    = null
				this.pappers_result  = null
				this.pending_pappers = null
				this.form_message    = ''
				this.processing      = false

				this._init_civility()
				this._init_phone()
				this._init_readonly()

				if (Number(this.form.tiers_moral) !== 1) {
					// Personne physique — vérifier si elle a déjà des activités
					if (tiers.tiers_id) {
						const activities = await this.checkTiersActivities(tiers.tiers_id)
						if (activities && activities.has_activities) {
							// Activités existantes : pas de conversion possible, contrôle de complétude direct
							if (this._is_physique_complete()) {
								this.$emit('submit', true)
								return
							}
							this.current_step = 'form'
							this.$refs['modal'].show()
							return
						}
					}
					// Pas d'activités ou tiers non encore créé → proposer la conversion
					this.current_step = 'confirm_physique'
					this.$refs['modal'].show()
				} else {
					// Personne morale → déterminer l'étape directement
					const needsModal = await this._determine_morale_step()
					if (needsModal) {
						this.$refs['modal'].show()
					}
				}
			},

			/** Initialise la civilité traduite depuis la valeur brute ('m' / 'mme'). Laisse vide si non reconnue ou absente. */
			_init_civility() {
				if (this.form.tiers_civility) {
					const translated = this.getTrad('civility.' + this.form.tiers_civility)
					this.form.tiers_civility = this.civilities.indexOf(translated) > -1 ? translated : ''
				} else {
					this.form.tiers_civility = ''
				}
			},

			/** Initialise les données téléphone depuis le premier numéro du tiers. */
			_init_phone() {
				// Détermine l'indicatif par défaut selon le pays du tiers
				const countryId = parseInt(this.form.tiers_country) || 75
				const iso3      = Countries.fr.iso_3[countryId] || 'FRA'
				const defaultIndicatif = Indicatif[iso3] || '+33'

				/**
				 * Convertit un code ISO2 en ISO3 via la table Countries.
				 * Retourne le code ISO3 du pays du formulaire si la conversion échoue.
				 * @param {string} code
				 * @returns {string}
				 */
				const toIso3 = (code) => {
					if (!code || code.length === 3) return code || iso3
					const isoTable  = Countries.fr.iso
					const iso3Table = Countries.fr.iso_3
					const id = Object.keys(isoTable).find(k => isoTable[k] === code)
					return id ? (iso3Table[id] || iso3) : iso3
				}

				if (Array.isArray(this.form.phones) && this.form.phones.length > 0 && !_isEmpty(this.form.phones[0].phone_numero)) {
					this.phone.phone_indicatif = this.form.phones[0].phone_indicatif || defaultIndicatif
					this.phone.phone_numero    = this.form.phones[0].phone_numero
					this.phone.phone_country   = toIso3(this.form.phones[0].phone_country) || iso3
				} else {
					this.phone = { phone_indicatif: defaultIndicatif, phone_numero: '', phone_country: iso3 }
				}
			},

			/** Construit la liste des champs en lecture seule selon les données déjà renseignées. */
			_init_readonly() {
				this.readonly = []
				const f = this.form
				if (!_isEmpty(f.tiers_civility))  this.readonly.push('civility')
				if (!_isEmpty(f.tiers_lastname))   this.readonly.push('lastname')
				if (!_isEmpty(f.tiers_firstname))  this.readonly.push('firstname')
				if (f.tiers_country && !_isEmpty(f.tiers_country.toString())) this.readonly.push('country')
				if (!_isEmpty(f.tiers_address1))   this.readonly.push('address1')
				if (!_isEmpty(f.tiers_address2))   this.readonly.push('address2')
				if (!_isEmpty(f.tiers_postalcode)) this.readonly.push('postalcode')
				if (!_isEmpty(f.tiers_town))       this.readonly.push('town')
				if (!_isEmpty(f.tiers_vat_number) && f.tiers_vatcheck !== 2) this.readonly.push('vat_number')
				if (Array.isArray(f.phones) && f.phones.length > 0 && !_isEmpty(f.phones[0].phone_numero)) {
					this.readonly.push('phone_number')
				}
			},

			/**
			 * Détermine l'étape adéquate pour un tiers traité comme personne morale.
			 * Si le tiers possède un SIRET mais des coordonnées incomplètes,
			 * interroge Pappers pour proposer de compléter les données.
			 * @returns {boolean} true si la modale doit être affichée.
			 */
			async _determine_morale_step() {
				const isFrance = parseInt(this.form.tiers_country) === 75

				if (isFrance) {
					if (this._has_siret()) {
						// Moral + France + SIRET : vérifier la complétude des coordonnées
						if (this._is_morale_complete()) {
							this.$emit('submit', true)
							return false
						}

						// Coordonnées incomplètes : interroger Pappers pour proposer la complétion
						try {
							const pappersResult = await this.searchPappers(this.form.tiers_siret)
							if (pappersResult) {
								this.siret_input_value = this.form.tiers_siret
								this.pappers_result = pappersResult
								this.current_step = 'pappers_confirm'
								return true
							}
						} catch (e) {
							console.error('InformationsTiersForm::_determine_morale_step => Pappers error', e)
						}

						// Pappers indisponible ou SIRET non trouvé : fallback sur le formulaire
						if (!_isEmpty(this.phone.phone_numero)) {
							this.current_step = 'form'
						} else {
							this.current_step = 'phone_only'
						}
						return true
					} else {
						this.current_step = 'siret_input'
						return true
					}
				} else {
					if (this._is_morale_complete()) {
						this.$emit('submit', true)
						return false
					}
					this.current_step = 'form'
					return true
				}
			},

			/** Vérifie si le tiers possède déjà un SIRET renseigné. */
			_has_siret() {
				return !_isEmpty(this.form.tiers_siret)
			},

			/** Vérifie la complétion minimale d'un tiers personne morale. */
			_is_morale_complete() {
				const f = this.form
				const base = !_isEmpty(f.tiers_rs)
					&& !_isEmpty(f.tiers_address1)
					&& !_isEmpty(f.tiers_postalcode)
					&& !_isEmpty(f.tiers_town)
					&& !!f.tiers_country
					&& !_isEmpty(this.phone.phone_numero)
				if (!base) return false
				// Pour les pays UE hors France, le numéro de TVA intracommunautaire est obligatoire
				if (this.is_vat_country) return !_isEmpty(f.tiers_vat_number)
				return true
			},

			/** Vérifie la complétion minimale d'un tiers personne physique. */
			_is_physique_complete() {
				const f = this.form
				return !_isEmpty(f.tiers_civility)
					&& !_isEmpty(f.tiers_firstname)
					&& !_isEmpty(f.tiers_lastname)
					&& !_isEmpty(f.tiers_address1)
					&& !_isEmpty(f.tiers_postalcode)
					&& !_isEmpty(f.tiers_town)
					&& !!f.tiers_country
					&& !_isEmpty(this.phone.phone_numero)
			},

			/**
			 * Callback confirmation personne physique.
			 * @param {boolean} confirmed - true = particulier confirmé, false = traiter comme société.
			 */
			async on_confirm_physique(confirmed) {
				if (confirmed) {
					if (this._is_physique_complete()) {
						this.$refs['modal'].hide()
						this.$emit('submit', true)
					} else {
						this.current_step = 'form'
					}
				} else {
					this.treat_as_morale = true
					this.form.tiers_rs = ''
					this.readonly = []
					this.current_step = null
					const needsModal = await this._determine_morale_step()
					if (!needsModal) {
						this.$refs['modal'].hide()
					}
				}
			},

			/**
			 * Valide le SIRET saisi (14 chiffres),
			 * vérifie s'il existe déjà sur la licence, puis interroge l'API Pappers.
			 */
			async submit_siret() {
				if (this.siret_loading) return

				const siret = this.siret_input_value.replace(/\s/g, '')
				if (!/^\d{14}$/.test(siret)) {
					this.siret_error = 'tiers.siret_invalid'
					return
				}

				this.siret_error      = ''
				this.siret_loading    = true
				this.form.tiers_siret = siret

				try {
					// 1. Vérifier si le SIRET est déjà utilisé sur la licence
					const siretCheck = await this.checkSiretOnLicence(siret, this.form.tiers_id || 0)
					if (siretCheck && siretCheck.exists) {
						// En mode création, bloquer dans tous les cas pour éviter les doublons.
						// En complétion d'un tiers existant, ne bloquer que si le SIRET appartient à un autre tiers.
						if (this.create_mode || !siretCheck.is_own_tiers) {
							this.current_step  = 'siret_exists'
							this.siret_loading = false
							return
						}
					}

					// 2. Interroger Pappers
					const pappersResult = await this.searchPappers(siret)
					if (!pappersResult) {
						this.current_step = 'pappers_impossible'
					} else {
						this.pappers_result = pappersResult
						this.current_step   = 'pappers_confirm'
					}
				} catch (e) {
					console.error('InformationsTiersForm::submit_siret => ERROR', e)
					this.current_step = 'pappers_impossible'
				} finally {
					this.siret_loading = false
				}
			},

			/**
			 * Confirme les données Pappers.
			 * Si le tiers physique a déjà des contrats/factures : crée un nouveau tiers moral.
			 * Sinon : met à jour le tiers existant (conversion physique → morale).
			 */
			async confirm_pappers() {
				if (this.processing) return
				this.processing = true

				const r    = this.pappers_result
				const siret = r.siret || this.siret_input_value.replace(/\s/g, '')

				// Préparer les données Pappers communes
				const pappersData = {
					siret,
					siren:      siret.substring(0, 9),
					rs:         r.nom_entreprise || '',
					address1:   (r.siege && r.siege.adresse_ligne_1) || '',
					address2:   (r.siege && r.siege.adresse_ligne_2) || '',
					postalcode: (r.siege && r.siege.code_postal) || '',
					town:       (r.siege && r.siege.ville) || '',
				}

				if (!_isEmpty(this.phone.phone_numero)) {
					const saved = await this._save_pappers_with_phone(pappersData)
					this.processing = false
					if (saved) {
						this.$refs['modal'].hide()
						this.$emit('submit', saved === true ? true : saved)
					} else {
						this.failureToast('toast.info_save_failed')
						this.$emit('submit', false)
					}
				} else {
					// Stocker les données pappers pour les utiliser après saisie du téléphone
					this.pending_pappers = pappersData
					this.processing       = false
					this.current_step     = 'phone_only'
				}
			},

			/**
			 * Vérifie si le tiers a des activités, puis choisit entre mise à jour ou création.
			 * @param {Object} pappersData - Données Pappers (siret, rs, address1, address2, postalcode, town)
			 * @returns {boolean|Object} true si mis à jour, l'objet nouveau tiers si créé, false en cas d'échec.
			 */
			async _save_pappers_with_phone(pappersData) {
				const phoneData = { ...this.phone }

				// Si aucun tiers existant n'est associé au formulaire (mode création ou tiers
				// connecté absent), on crée systématiquement un nouveau tiers via Pappers
				// pour éviter d'appeler la route d'édition avec un tiers_id undefined.
				if (this.create_mode || !this.form.tiers_id) {
					const newTiers = await this.createTiersFromPappers({
						...pappersData,
						phone: phoneData,
					})
					return newTiers || false
				}

				// On ne crée un nouveau tiers que si on convertit un physique avec activités existantes.
				// Pour un tiers déjà moral (treat_as_morale=false), on met toujours à jour.
				if (this.treat_as_morale) {
					const activities = await this.checkTiersActivities(this.form.tiers_id)
					if (activities && activities.has_activities) {
						// Créer un nouveau tiers moral — on ne touche pas aux données du tiers physique existant
						const newTiers = await this.createTiersFromPappers({
							...pappersData,
							phone: phoneData,
						})
						return newTiers || false
					}
				}

				// Mettre à jour le tiers existant
				this.form.tiers_rs         = pappersData.rs
				this.form.tiers_address1   = pappersData.address1
				this.form.tiers_address2   = pappersData.address2
				this.form.tiers_postalcode = pappersData.postalcode
				this.form.tiers_town       = pappersData.town
				this.form.tiers_country    = 75
				this.form.tiers_siret      = pappersData.siret
				this.form.tiers_siren      = pappersData.siren
				this.form.tiers_moral      = 1
				const params = _cloneDeep(this.form)
				params.phone = phoneData
				params.tiers_lang = this.$store.state.user.lang
				const result = await this.editTiers(params)
				// Propager les sentinelles d'erreur sans les avaler
				if (result === 'phone_invalid' || result === 'vat_duplicate') return result
				if (result) this.form.phones = [{ ...this.phone }]
				return result ? this.form : false
			},

			/** Annule la confirmation Pappers et revient à la saisie SIRET. */
			cancel_pappers() {
				this.current_step = 'siret_input'
			},

			/** Ferme la modale et émet l'événement de redirection vers le contact. */
			go_contact_us() {
				this.$refs['modal'].hide()
				this.$emit('contact-us')
			},

			/** Ferme la modale et signale l'échec pour désélectionner le tiers. */
			on_impossible_choose_other() {
				this.$refs['modal'].hide()
				this.$emit('submit', false)
			},

			/**
			 * Valide et sauvegarde le numéro de téléphone (étape après confirmation Pappers ou après la morale).
			 */
			async submit_phone_only() {
				if (this.processing) return
				this.error = []

				if (_isEmpty(this.phone.phone_numero)) {
					this.error.push('phone')
					return
				}

				this.processing = true

				// Si on est en attente de données Pappers (cas confirm_pappers sans téléphone)
				if (this.pending_pappers) {
					const saved = await this._save_pappers_with_phone(this.pending_pappers)
					if (saved === 'phone_invalid') {
						this.processing = false
						this.error = ['phone']
						this.phone_invalid_format = true
						return
					}
					if (saved === 'vat_duplicate') {
						this.processing = false
						this.pending_pappers = null
						this.current_step = 'vat_exists'
						return
					}
					this.pending_pappers = null
					this.processing = false
					if (saved) {
						this.$refs['modal'].hide()
						this.$emit('submit', saved === true ? true : saved)
					} else {
						this.failureToast('toast.info_save_failed')
						this.$emit('submit', false)
					}
					return
				}

				// Cas standard : complétion du tiers existant
				const params    = _cloneDeep(this.form)
				params.phone    = this.phone
				params.tiers_moral = this.effective_tiers_moral
				params.tiers_lang  = this.$store.state.user.lang
				const result    = await this.editTiers(params)
				this.processing = false

				if (result === 'phone_invalid') {
					this.error = ['phone']
					this.phone_invalid_format = true
				} else if (result === 'vat_duplicate') {
					this.current_step = 'vat_exists'
				} else if (result) {
					this.phone_invalid_format = false
					this.form.tiers_moral = this.effective_tiers_moral
					this.form.phones = [{ ...this.phone }]
					this.$refs['modal'].hide()
					this.$emit('submit', this.form)
				} else {
					this.failureToast('toast.info_save_failed')
					this.$emit('submit', false)
				}
			},

			checkForm: async function () {
				if (!this.processing) {
					this.processing = true
					this.form_message = ""
					this.error = []

					if (!this.form.tiers_country) {
						this.error.push("country")
					}
					if (_isEmpty(this.form.tiers_address1)) {
						this.error.push("address1")
					}
					if (_isEmpty(this.form.tiers_postalcode)) {
						this.error.push("postalcode")
					}
					if (_isEmpty(this.form.tiers_town)) {
						this.error.push("town")
					}
					if (_isEmpty(this.phone.phone_numero)) {
						this.error.push("phone")
					}

					if (this.effective_tiers_moral === 1) {
						if (_isEmpty(this.form.tiers_rs)) {
							this.error.push("rs")
						}
					} else {
						if (_isEmpty(this.form.tiers_firstname)) this.error.push("firstname")
						if (_isEmpty(this.form.tiers_lastname))  this.error.push("lastname")
						if (_isEmpty(this.form.tiers_civility))  this.error.push("civility")
					}

					if (this.hasRegions && _isEmpty(this.form.tiers_region)) {
						this.error.push("region")
					}

					if (this.effective_tiers_moral === 1 && this.is_vat_country && _isEmpty(this.form.tiers_vat_number)) {
						this.error.push("vat_number")
					}
					this.form.tiers_vat_number = (this.form.tiers_vat_number || '').replace(/\W/g, '').toUpperCase()
					if (this.form.tiers_vat_number && !this.checkVAT(this.form.tiers_vat_number, this.form.tiers_country)) {
						this.error.push("vat_number")
					}

					if (this.error.length === 0) {
						// Vérifier si la TVA est déjà utilisée par un autre tiers sur la licence
						if (this.form.tiers_vat_number) {
							const vatCheck = await this.checkVatOnLicence(this.form.tiers_vat_number, this.form.tiers_id)
							if (vatCheck && vatCheck.exists && !vatCheck.is_own_tiers) {
								this.current_step = 'vat_exists'
								this.processing = false
								return
							}
						}

						if (this.effective_tiers_moral !== 1) {
							if (this.form.tiers_civility === this.getTrad('civility.m')) {
								this.form.tiers_civility = 'm'
							} else if (this.form.tiers_civility === this.getTrad('civility.mme')) {
								this.form.tiers_civility = 'mme'
							}
						}
						const params = _cloneDeep(this.form)
						params.phone = this.phone
						params.tiers_moral = this.effective_tiers_moral
						params.tiers_lang = this.$store.state.user.lang
						let result = await this.editTiers(params)
						this.processing = false

						if (result === 'phone_invalid') {
							this.error = ['phone']
							this.phone_invalid_format = true
							return
						}

						if (result === 'vat_duplicate') {
							this.current_step = 'vat_exists'
							return
						}

						if (!result) {
							this.failureToast("toast.info_save_failed")
							this.$emit('submit', false)
						} else {
							this.form.tiers_moral = this.effective_tiers_moral
							this.form.phones = [{ ...this.phone }]
							this.$refs['modal'].hide()
							this.$emit('submit', this.form)
						}
					} else {
						this.processing = false
						this.form_message = "formulaire.erreur_champs_non_remplis"
					}
				}
            },

            checkSpecialChar() {
            	this.error = []
				let format = /^[A-Z- ]*$/;

				if(!format.test(this.form.tiers_firstname)) {
					this.error.push("firstname") 
				}
				if(!format.test(this.form.tiers_lastname)) {
					this.error.push("lastname")
				}

				if(!format.test(this.form.tiers_town)) {
					this.error.push("town")
				}

				format = /^[0-9 ]*$/;
				if(!format.test(this.form.tiers_postalcode)) {
					this.error.push("postalcode")
				}

				format = /^[A-Za-z0-9 ]*$/;
				if(!format.test(this.form.tiers_address1)) {
					this.error.push("address1")
				}

				if(!format.test(this.form.tiers_address2)) {
					this.error.push("address2")
				}
            }
        },
		watch: {
			'form.tiers_firstname' (val) {
				if(val) {
					this.form.tiers_firstname = val.toUpperCase()
				}
			},
			'form.tiers_lastname' (val) {
				if(val) {
					this.form.tiers_lastname = val.toUpperCase()
				}
			},
			'form.tiers_town' (val) {
				if(val) {
					this.form.tiers_town = val.toUpperCase()
				}
			},
			'form.tiers_address1' (val) {
				if(val) {
					this.form.tiers_address1 = val.toUpperCase()
				}
			},
			'form.tiers_address2' (val) {
				if(val) {
					this.form.tiers_address2 = val.toUpperCase()
				}
			},
			'phone.phone_country' (val) {
				if(val && Indicatif[val]) {
					this.phone.phone_indicatif = Indicatif[val]
				}
			}
		},
		components: {
			LoadingSpinner : () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			ErrorAlert : () => import('GroomyRoot/components/Alert/ErrorAlert'),
            CountryInput : () => import('GroomyRoot/components/Inputs/CountryInput'),
            WarnAlert : () => import('GroomyRoot/components/Alert/WarnAlert'),
			PhoneInput : () => import('GroomyRoot/components/Inputs/PhoneInput')
		}
	}
</script>